import React, { useState } from "react";
import PropTypes from "prop-types";
import { Grid, GridItem } from "@domestika-dev/prudencio/dist/atoms/Layout";
import { validateRatingAnswer, validateBinaryAnswer } from "../../utils/validations";
import * as LiveFeedbackQuestionTypes from "../../constants/liveFeedbackQuestionTypes";
import { getLang } from "../../../../../js/utils/functions";
import { LiveFeedbackRatingQuestion } from "./LiveFeedbackRatingQuestion";
import { LiveFeedbackYesNoQuestion } from "./LiveFeedbackYesNoQuestion";

const MAX_RATING = 5;
const MIN_RATING = 1;
const YES_NO_VALUES = ["yes", "no"];

const validateAnswer = (value, type) => {
  return {
    [LiveFeedbackQuestionTypes.RATING]: (answerValue) =>
      validateRatingAnswer(answerValue, MAX_RATING, MIN_RATING),
    [LiveFeedbackQuestionTypes.YES_NO]: (answerValue) =>
      validateBinaryAnswer(answerValue, YES_NO_VALUES),
  }[type](value);
};

export const LiveFeedbackQuestions = ({ liveFeedbackQuestions, onAnswersChanged }) => {
  const [answers, setAnswers] = useState();

  const updateAnswers = (newAnswers) => {
    setAnswers(newAnswers);
    onAnswersChanged(newAnswers);
  };

  const setAnswer = ({ questionId, value, type, locale = getLang}) => {
    const updatedAnswers = answers ? [...answers] : [];
    const answerIndex = updatedAnswers.findIndex(
      ({ liveFeedbackQuestionId }) => liveFeedbackQuestionId === questionId
    );

    const valid = validateAnswer(value, type);

    if (!valid && answerIndex >= 0) {
      updatedAnswers.splice(answerIndex, 1);
    }

    if (valid && answerIndex >= 0) {
      updatedAnswers[answerIndex] = {
        liveFeedbackQuestionId: questionId,
        answer: value,
        locale,
      };
    }

    if (valid && answerIndex < 0) {
      updatedAnswers.push({
        liveFeedbackQuestionId: questionId,
        answer: value,
        locale,
      });
    }

    updateAnswers(updatedAnswers);
  };

  const sortQuestions = ({ order: orderA }, { order: orderB }) => orderA - orderB;

  const getQuestion = ({ questionType, question, id }, index) => (
    <GridItem gridColumn="1 / span 8" gridRow={index + 1} key={id}>
      {
        {
          [LiveFeedbackQuestionTypes.RATING]: (
            <LiveFeedbackRatingQuestion
              question={question}
              maxRating={MAX_RATING}
              onValueChanged={({ value }) =>
                setAnswer({ questionId: id, value, type: LiveFeedbackQuestionTypes.RATING })
              }
            />
          ),
          [LiveFeedbackQuestionTypes.YES_NO]: (
            <LiveFeedbackYesNoQuestion
              question={question}
              onValueChanged={({ value }) =>
                setAnswer({ questionId: id, value, type: LiveFeedbackQuestionTypes.YES_NO })
              }
            />
          ),
        }[questionType]
      }
    </GridItem>
  );

  return (
    <GridItem gridColumn="1 / span 8" gridRow="2" marginTop={5}>
      <Grid
        gridRowGap={[5, 5, 5]}
        gridTemplateColumns="repeat(2, minmax(100px, 1fr))"
        style={{ justifyItems: "center" }}
      >
        {liveFeedbackQuestions.sort(sortQuestions).map(getQuestion)}
      </Grid>
    </GridItem>
  );
};

LiveFeedbackQuestions.propTypes = {
  liveFeedbackQuestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      question: PropTypes.string.isRequired,
      questionType: PropTypes.oneOf([
        LiveFeedbackQuestionTypes.RATING,
        LiveFeedbackQuestionTypes.YES_NO,
      ]).isRequired,
      order: PropTypes.number.isRequired,
    })
  ).isRequired,
  onAnswersChanged: PropTypes.func.isRequired,
};

export default LiveFeedbackQuestions;
